import PropTypes from "prop-types";
import React from "react";
import styled from "styled-components";
import InternalLink from "components/InternalLink";

import { formatDate } from "../../../../../utils/dateTool";

const TopicGridTitle = styled.h2`
    font-size: 2.4rem;
    letter-spacing: -0.01em;
    margin-bottom: 40px;
    text-transform: uppercase;
    > a {
        color: #15181a;
    }
`;

const TopicGridLink = styled(InternalLink)`
    display: flex;
    gap: 24px;
    color: #15181a;
    &:hover {
        text-decoration: none;
        opacity: 0.7;
    }
`;

const CardContainer = styled.section`
    display: grid;
    gap: 32px 40px;
    grid-template-columns: 1fr 1fr;
    @media (max-width: 991px) {
        grid-template-columns: 1fr;
    }
`;

const TopicGridFooter = styled.footer`
    border-top: 1px solid #e6e6e6;
    margin-top: 48px;
    padding-top: 20px;
    text-align: right;
    > a {
        align-items: center;
        display: inline-flex;
        font-weight: 600;
        letter-spacing: -0.011em;
        color: #15181a;
        :hover {
            text-decoration: none;
            opacity: 0.7;
        }
    }
    img {
        height: 16px;
        margin-left: 4px;
        width: 16px;
    }
`;

const TopicGridRest = styled.article`
    max-width: 720px;
    width: 100%;
    @media (max-width: 991px) {
        grid-template-columns: 1fr;
    }
`;

const CardTitle = styled.h2`
    font-size: 1.6rem;
    letter-spacing: -0.015em;
    line-height: 1.3;
    margin: 0;
`;

const FirstCard = styled.article`
    grid-column: 1/3;
    border-bottom: 1px solid #e6e6e6;
    margin-bottom: 16px;
    padding-bottom: 48px;
    width: 100%;
    word-break: break-word;
    @media (max-width: 991px) {
        grid-column: auto;
    }
`;

const FirstCardFigure = styled.figure`
    position: relative;
    width: 100%;
    height: 100%;
    flex: 1;
    &:before {
        content: "";
        padding-bottom: 60%;
        display: block;
    }
    > img {
        display: block;
        height: 100%;
        left: 0;
        object-fit: cover;
        position: absolute;
        top: 0;
        width: 100%;
    }
`;

const FirstCardTitle = styled(CardTitle)`
    font-size: 2.8rem;
    letter-spacing: -0.02em;
`;

const FirstCardContent = styled.div`
    font-size: 1.7rem;
    margin-top: 12px;
    font-family: Lora, Georgia, Times, serif;
`;

const FirstCardLink = styled(TopicGridLink)`
    display: flex;
    gap: 40px;
    > * {
        flex: 1;
    }
    @media (max-width: 991px) {
        flex-direction: column;
        > * {
            width: 100%;
        }
        img {
            height: 100%;
            width: 100%;
        }
    }
`;

const CardInfo = styled.div`
    align-items: center;
    color: #999999;
    display: flex;
    font-size: 1.1rem;
    font-weight: 500;
    gap: 6px;
    margin-top: 16px;
    text-transform: uppercase;
`;

const CardFigure = styled.figure`
    position: relative;
    height: 100%;
    flex-shrink: 0;
    height: 100%;
    width: 128px;
    @media (max-width: 991px) {
        width: 100px;
    }
    &:before {
        content: "";
        display: block;
        padding-bottom: 70%;
    }
    > img {
        height: 100%;
        left: 0;
        object-fit: cover;
        position: absolute;
        top: 0;
        width: 100%;
    }
`;

const InfoSep = styled.span`
    background-color: #999999;
    border-radius: 50%;
    height: 2px;
    width: 2px;
`;

const TopicGrid = ({ data, tag = "latest", link = "page/2" }) => {
    return (
        data.length && (
            <section className="gh-topic-grid">
                <TopicGridTitle>
                    <TopicGridLink to={link}>
                        <span>{tag}</span>
                    </TopicGridLink>
                </TopicGridTitle>
                <CardContainer>
                    <FirstCard>
                        <FirstCardLink to={`/${data[0].node.slug}/`}>
                            {data[0].node.feature_image && (
                                <FirstCardFigure>
                                    <img
                                        src={data[0].node.feature_image}
                                        alt={data[0].node.title}
                                    />
                                </FirstCardFigure>
                            )}
                            <div>
                                <FirstCardTitle>
                                    {data[0].node.title}
                                </FirstCardTitle>
                                <FirstCardContent>
                                    {data[0].node.excerpt}
                                </FirstCardContent>
                                <CardInfo>
                                    {"by "}
                                    <span>{data[0].node.authors[0].name}</span>
                                    <InfoSep />
                                    <span>
                                        {formatDate(data[0].node.published_at)}
                                    </span>
                                </CardInfo>
                            </div>
                        </FirstCardLink>
                    </FirstCard>
                    {data.map((article) => {
                        return (
                            article !== data[0] && (
                                <TopicGridRest key={article.node.slug}>
                                    <TopicGridLink
                                        to={`/${article.node.slug}/`}
                                    >
                                        {article.node.feature_image && (
                                            <CardFigure>
                                                <img
                                                    src={
                                                        article.node
                                                            .feature_image
                                                    }
                                                    alt={article.node.title}
                                                />
                                            </CardFigure>
                                        )}
                                        <div>
                                            <CardTitle>
                                                {article.node.title}
                                            </CardTitle>
                                            <CardInfo>
                                                {"by "}
                                                <span>
                                                    {
                                                        article.node.authors[0]
                                                            .name
                                                    }
                                                </span>
                                                <InfoSep />
                                                <span>
                                                    {formatDate(
                                                        article.node
                                                            .published_at
                                                    )}
                                                </span>
                                            </CardInfo>
                                        </div>
                                    </TopicGridLink>
                                </TopicGridRest>
                            )
                        );
                    })}
                </CardContainer>
                <TopicGridFooter>
                    <a href={link}>
                        {"Show more"}
                        <img src={"/icons/arrow-right.svg"} alt="arrow-right" />
                    </a>
                </TopicGridFooter>
            </section>
        )
    );
};

TopicGrid.propTypes = {
    data: PropTypes.array,
    site: PropTypes.shape({
        description: PropTypes.string,
        logo: PropTypes.string,
        title: PropTypes.string,
        navigation: PropTypes.array,
    }),
    tag: PropTypes.string,
    link: PropTypes.string,
};

export default TopicGrid;
